import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Check, X, Prohibit, ClipboardText, Copy } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';

import { notify } from '../lib/notify';
import { TableRowsSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  revoked: 'bg-gray-200 text-gray-700',
  used: 'bg-blue-100 text-blue-700',
  expired: 'bg-gray-100 text-gray-500',
};

const NSM_AND_ABOVE = ['National Sales Manager', 'Vice President', 'President'];

const GstExemptions = () => {
  const { user } = useAuthStore();
  const [scope, setScope] = useState('mine');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // { request, action: 'approve'|'reject'|'revoke' }
  const [decisionReason, setDecisionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canRevoke = NSM_AND_ABOVE.includes(user?.role);

  const tabs = [
    { key: 'mine', label: 'My Requests' },
    { key: 'pending', label: 'Pending My Approval' },
    ...(canRevoke ? [{ key: 'all', label: 'All Requests' }] : []),
  ];

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gst-exemptions`, { params: { scope } });
      setItems(r.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const handleDecisionSubmit = async () => {
    if (!decisionReason.trim()) {
      notify('Please provide a reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/gst-exemptions/${decision.request.id}/${decision.action}`,
        { reason: decisionReason.trim() }
      );
      setDecision(null);
      setDecisionReason('');
      load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    notify(`Copied code: ${code}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="gst-exemptions-page">
      <div className="mb-6">
        <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
          GST Exemptions
        </h1>
        <p className="text-[#6D8E87]">Request and manage GST bypass for customers without GST registration</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#D5E8E4]" data-testid="gst-exemption-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setScope(t.key)}
            className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition ${
              scope === t.key
                ? 'border-[#00a69c] text-[#00a69c]'
                : 'border-transparent text-[#6D8E87] hover:text-[#052A23]'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="medical-card overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="gst-exemptions-table">
          <thead className="bg-[#F4F9F7]">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Organisation</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Requester</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Reason</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Approver</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Status</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Code</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableRowsSkeleton cols={7} />
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6D8E87]" data-testid="no-exemptions">
                No GST exemption requests in this view.
              </td></tr>
            ) : items.map(it => {
              const isMine = it.requester_id === user?.id;
              const isApprover = it.approver_role === user?.role && it.status === 'pending';
              const canRevokeThis = canRevoke && it.status === 'approved';
              return (
                <tr key={it.id} className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7]" data-testid={`exemption-row-${it.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-[#052A23]" data-testid={`org-${it.id}`}>{it.organisation_name}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-[#052A23]">{it.requester_name}</div>
                    <div className="text-xs text-[#6D8E87]">{it.requester_role}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#3B5A54] max-w-xs truncate" title={it.reason}>{it.reason}</td>
                  <td className="px-4 py-3 text-sm">
                    {it.approver_name ? (
                      <>
                        <div className="text-[#052A23]">{it.approver_name}</div>
                        <div className="text-xs text-[#6D8E87]">{it.approver_role}</div>
                      </>
                    ) : (
                      <span className="text-xs text-[#6D8E87] italic">Awaiting {it.approver_role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[it.status]}`} data-testid={`status-${it.id}`}>
                      {it.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.bypass_code && isMine && it.status === 'approved' ? (
                      <button
                        onClick={() => copyCode(it.bypass_code)}
                        className="flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-300 rounded font-mono text-sm text-emerald-700 hover:bg-emerald-100"
                        data-testid={`code-${it.id}`}
                      >
                        {it.bypass_code} <Copy size={12} />
                      </button>
                    ) : it.bypass_code ? (
                      <span className="font-mono text-xs text-[#6D8E87]">●●●●●●●●</span>
                    ) : (
                      <span className="text-xs text-[#6D8E87]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      {isApprover && (
                        <>
                          <button
                            onClick={() => { setDecision({ request: it, action: 'approve' }); setDecisionReason(''); }}
                            className="flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700"
                            data-testid={`approve-${it.id}`}
                          >
                            <Check size={12} /> Approve
                          </button>
                          <button
                            onClick={() => { setDecision({ request: it, action: 'reject' }); setDecisionReason(''); }}
                            className="flex items-center gap-1 px-3 py-1 bg-rose-600 text-white rounded text-xs font-medium hover:bg-rose-700"
                            data-testid={`reject-${it.id}`}
                          >
                            <X size={12} /> Reject
                          </button>
                        </>
                      )}
                      {canRevokeThis && !isApprover && (
                        <button
                          onClick={() => { setDecision({ request: it, action: 'revoke' }); setDecisionReason(''); }}
                          className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded text-xs font-medium hover:bg-gray-800"
                          data-testid={`revoke-${it.id}`}
                        >
                          <Prohibit size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Decision Modal */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) { setDecision(null); setDecisionReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#052A23] capitalize" style={{ fontFamily: 'Outfit' }}>
              {decision?.action} GST Exemption
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-[#F4F9F7] rounded-lg text-sm">
                <div className="font-medium text-[#052A23]">{decision.request.organisation_name}</div>
                <div className="text-xs text-[#6D8E87] mt-1">Requested by {decision.request.requester_name} ({decision.request.requester_role})</div>
                <div className="text-xs text-[#3B5A54] mt-2">Reason: {decision.request.reason}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">
                  Reason for {decision.action} *
                </label>
                <textarea
                  value={decisionReason}
                  onChange={(e) => setDecisionReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  placeholder={`Why are you ${decision.action === 'approve' ? 'approving' : decision.action === 'reject' ? 'rejecting' : 'revoking'} this?`}
                  data-testid="decision-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setDecision(null); setDecisionReason(''); }}
                  className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7]"
                  data-testid="cancel-decision-btn"
                >Cancel</button>
                <button
                  onClick={handleDecisionSubmit}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
                    decision.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' :
                    decision.action === 'reject' ? 'bg-rose-600 hover:bg-rose-700' :
                    'bg-gray-700 hover:bg-gray-800'
                  }`}
                  data-testid="confirm-decision-btn"
                >
                  {submitting ? 'Submitting...' : `Confirm ${decision.action}`}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GstExemptions;
